// Products CRUD (BUILD_SPEC.md §5.2) — services/api/src/modules/config/
// products.ts. Server allows OPERATIONS or ADMIN to write; this Settings screen
// still gates its own write UI to ADMIN (Settings is ADMIN-focused per plan.md
// G26's goal text — see permissions.ts). Pack-spec editing (the nested
// bulk-product + qty-per-pack + packaging resource, §5.2) is a separate modal
// (PackSpecModal.tsx) reachable per row, since not every product is a pack SKU.
import { useState } from 'react';
import { Alert, Badge, Button, Group, Modal, NumberInput, Paper, Select, Stack, Switch, Table, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconBox, IconPackageOff, IconPlus } from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';
import { BASE_UOMS, PRODUCT_TYPES, VAT_TREATMENTS, type ProductCreateInput } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { EmptyState } from '../../../components/EmptyState';
import { parseNum } from '../../../lib/parseNum';
import { useIsAdmin } from '../permissions';
import { useCreateProduct, useProducts, useUpdateProduct } from '../api';
import type { ProductDto } from '../types';
import { PackSpecModal } from './PackSpecModal';

interface ProductFormValues {
  sku: string;
  name: string;
  type: ProductDto['type'];
  baseUom: ProductDto['baseUom'];
  isMoistureTracked: boolean;
  defaultMcPct: number | string;
  bulkDensityKgM3: number | string;
  vatTreatment: ProductDto['vatTreatment'];
  minLevel: number | string;
  reorderQty: number | string;
  xeroSalesAccount: string;
  xeroPurchaseAccount: string;
  isActive: boolean;
}

function toFormValues(row?: ProductDto): ProductFormValues {
  return {
    sku: row?.sku ?? '',
    name: row?.name ?? '',
    type: row?.type ?? 'RAW_MATERIAL',
    baseUom: row?.baseUom ?? 'KG',
    isMoistureTracked: row?.isMoistureTracked ?? false,
    defaultMcPct: row?.defaultMcPct ? Number(row.defaultMcPct) : '',
    bulkDensityKgM3: row?.bulkDensityKgM3 ? Number(row.bulkDensityKgM3) : '',
    vatTreatment: row?.vatTreatment ?? 'STANDARD',
    minLevel: row?.minLevel ? Number(row.minLevel) : '',
    reorderQty: row?.reorderQty ? Number(row.reorderQty) : '',
    xeroSalesAccount: row?.xeroSalesAccount ?? '',
    xeroPurchaseAccount: row?.xeroPurchaseAccount ?? '',
    isActive: row?.isActive ?? true,
  };
}

function toInput(values: ProductFormValues): ProductCreateInput {
  const defaultMcPct = parseNum(values.defaultMcPct);
  const bulkDensityKgM3 = parseNum(values.bulkDensityKgM3);
  const minLevel = parseNum(values.minLevel);
  const reorderQty = parseNum(values.reorderQty);
  return {
    sku: values.sku.trim(),
    name: values.name.trim(),
    type: values.type,
    baseUom: values.baseUom,
    isMoistureTracked: values.isMoistureTracked,
    defaultMcPct: values.isMoistureTracked && defaultMcPct !== null ? defaultMcPct.toFixed(2) : null,
    bulkDensityKgM3: bulkDensityKgM3 !== null ? bulkDensityKgM3.toFixed(3) : null,
    vatTreatment: values.vatTreatment,
    minLevel: minLevel !== null ? minLevel.toFixed(3) : null,
    reorderQty: reorderQty !== null ? reorderQty.toFixed(3) : null,
    xeroSalesAccount: values.xeroSalesAccount.trim() || null,
    xeroPurchaseAccount: values.xeroPurchaseAccount.trim() || null,
    isActive: values.isActive,
  };
}

function ProductFormModal({ opened, onClose, editing }: { opened: boolean; onClose: () => void; editing: ProductDto | null }) {
  const create = useCreateProduct();
  const update = useUpdateProduct(editing?.id ?? '');
  const mutation = editing ? update : create;

  const form = useForm<ProductFormValues>({
    initialValues: toFormValues(editing ?? undefined),
    validate: {
      sku: (v) => (v.trim() ? null : 'SKU is required'),
      name: (v) => (v.trim() ? null : 'Name is required'),
      defaultMcPct: (v, values) => {
        const n = parseNum(v);
        return values.isMoistureTracked && n !== null && (n < 0 || n > 100) ? 'Must be between 0 and 100' : null;
      },
    },
  });

  function handleSubmit(values: ProductFormValues) {
    mutation.mutate(toInput(values), { onSuccess: () => onClose() });
  }

  return (
    <Modal opened={opened} onClose={onClose} title={editing ? `Edit product ${editing.sku}` : 'New product'} centered size="lg">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          {mutation.isError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {mutation.error instanceof ApiRequestError ? mutation.error.message : 'Failed to save product.'}
            </Alert>
          )}
          <Group grow>
            <TextInput label="SKU" required {...form.getInputProps('sku')} />
            <TextInput label="Name" required {...form.getInputProps('name')} />
          </Group>
          <Group grow>
            <Select label="Type" description="Classifies the product for reporting — doesn't change how it's counted or costed." data={[...PRODUCT_TYPES]} required {...form.getInputProps('type')} />
            <Select
              label="Base UOM"
              description="Whether this product is weighed (KG) or counted (EACH) throughout the app — dispatches, receipts and counts all follow this unit."
              data={[...BASE_UOMS]}
              required
              {...form.getInputProps('baseUom')}
            />
            <Select
              label="VAT treatment"
              description="Default VAT applied to this product's lines — still editable per line on any document."
              data={[...VAT_TREATMENTS]}
              required
              {...form.getInputProps('vatTreatment')}
            />
          </Group>

          <Switch
            label="Moisture tracked"
            description="Turn on for products whose moisture content affects dry mass and yield — adds a moisture % field to receipts and production runs."
            {...form.getInputProps('isMoistureTracked', { type: 'checkbox' })}
          />
          {form.values.isMoistureTracked && (
            <NumberInput
              label="Default moisture %"
              description="Prefills the moisture reading on new receipts for this product — always editable per receipt."
              min={0}
              max={100}
              decimalScale={2}
              value={form.values.defaultMcPct}
              onChange={(v) => form.setFieldValue('defaultMcPct', v)}
              error={form.errors.defaultMcPct}
            />
          )}

          <Group grow>
            <NumberInput
              label="Bulk density (kg/m³)"
              description="Used by silo calibration when density source = product"
              min={0}
              decimalScale={3}
              value={form.values.bulkDensityKgM3}
              onChange={(v) => form.setFieldValue('bulkDensityKgM3', v)}
            />
          </Group>

          <Group grow>
            <NumberInput
              label="Reorder point (min level)"
              description="Stock at or below this level flags the product on the reorder suggestions report."
              min={0}
              decimalScale={3}
              value={form.values.minLevel}
              onChange={(v) => form.setFieldValue('minLevel', v)}
            />
            <NumberInput
              label="Reorder quantity"
              description="Suggested quantity to reorder once stock falls below the reorder point."
              min={0}
              decimalScale={3}
              value={form.values.reorderQty}
              onChange={(v) => form.setFieldValue('reorderQty', v)}
            />
          </Group>

          <Group grow>
            <TextInput
              label="Xero sales account (optional)"
              description="Overrides the tenant's default sales account mapping for this product's invoice lines."
              {...form.getInputProps('xeroSalesAccount')}
            />
            <TextInput
              label="Xero purchase account (optional)"
              description="Overrides the tenant's default purchase account mapping for this product's bill lines."
              {...form.getInputProps('xeroPurchaseAccount')}
            />
          </Group>

          <Switch
            label="Active"
            description="Inactive products are hidden from new dispatches, sales orders, receipts and counts — existing documents keep referencing them."
            {...form.getInputProps('isActive', { type: 'checkbox' })}
          />

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {editing ? 'Save changes' : 'Create product'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

export function ProductRegister() {
  const isAdmin = useIsAdmin();
  const { data, isLoading, isError } = useProducts();
  const [modalOpened, setModalOpened] = useState(false);
  const [editing, setEditing] = useState<ProductDto | null>(null);
  const [packSpecProduct, setPackSpecProduct] = useState<ProductDto | null>(null);
  const rows = data?.items ?? [];

  function openCreate() {
    setEditing(null);
    setModalOpened(true);
  }
  function openEdit(row: ProductDto) {
    setEditing(row);
    setModalOpened(true);
  }

  const newProductButton = isAdmin ? (
    <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
      New product
    </Button>
  ) : undefined;
  // Header/EmptyState CTA de-dupe (guidance-wave gap, 2026-08-26; found by
  // audit): on an empty register the EmptyState below already offers
  // `newProductButton` (plus the importer link) as the call-to-action —
  // showing the button in the page header too rendered it TWICE on a fresh
  // tenant. Reuses the exact emptiness condition the EmptyState branch checks.
  const showHeaderCreate = isLoading || rows.length > 0;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Products</Title>
        {showHeaderCreate && newProductButton}
      </Group>

      {isError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load products.
        </Alert>
      )}

      <Paper withBorder radius="md">
        <Table.ScrollContainer minWidth={780}>
          <Table striped highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>SKU</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>UOM</Table.Th>
                <Table.Th>Moisture</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((row) => (
                <Table.Tr key={row.id}>
                  <Table.Td>{row.sku}</Table.Td>
                  <Table.Td>{row.name}</Table.Td>
                  <Table.Td>{row.type}</Table.Td>
                  <Table.Td>{row.baseUom}</Table.Td>
                  <Table.Td>{row.isMoistureTracked ? `${row.defaultMcPct ?? '—'}%` : '—'}</Table.Td>
                  <Table.Td>
                    <Badge color={row.isActive ? 'brandGreen' : 'gray'} variant="light">
                      {row.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      {isAdmin && (
                        <Button variant="subtle" size="xs" onClick={() => openEdit(row)}>
                          Edit
                        </Button>
                      )}
                      <Button
                        variant="subtle"
                        size="xs"
                        leftSection={<IconBox size={14} />}
                        onClick={() => setPackSpecProduct(row)}
                      >
                        Pack spec
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <EmptyState
                      icon={IconPackageOff}
                      title="No products yet"
                      hint="Products normally arrive with the Unleashed import at cutover — or add one manually here."
                      action={
                        <Group gap="sm">
                          {newProductButton}
                          <Button component={Link} to="/settings/importer" variant="light">
                            Go to Importer
                          </Button>
                        </Group>
                      }
                    />
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      <ProductFormModal key={editing?.id ?? 'new'} opened={modalOpened} onClose={() => setModalOpened(false)} editing={editing} />

      {packSpecProduct && (
        <PackSpecModal
          key={packSpecProduct.id}
          opened
          onClose={() => setPackSpecProduct(null)}
          packProduct={packSpecProduct}
          allProducts={rows}
        />
      )}
    </Stack>
  );
}
